import { fromValue, map, mergeMap, pipe } from 'wonka';
import { getEnvironment, getOptions } from './environment';
import { Tag } from './tag';
import {
  buildCeltraClickTrackers,
  buildCeltraCreative$,
} from './third-party/celtra';
import { RE_GDPR, RE_GDPR_CONSENT } from './third-party/gdpr';
import { createIframeSrcdoc, getMarkupTemplates } from './utils/dom';
import { csvToArray } from './utils/string';
import { getUserPlatform } from './utils/user';
import { buster, processMacros } from './utils/util';
import { getPageSizeDetails } from './size';

/**
 * Regex for testing whether a line starts with a URL. This is used for detecting scripts in custom markup.
 */
const RE_URL_TEST = /^\s*https?\:\/\//i;

const handleCreativeTypes$ = (
  tag: Tag,
  markup: ReturnType<typeof getMarkupTemplates>,
) => {
  const payload = tag.payload!;
  // It's Celtra creative if we have a placementId
  if (payload.creative.placementId) {
    return buildCeltraCreative$(tag, markup);
  }

  // Non celtra creative, but we have markup
  if (payload.creative.markup) {
    return fromValue({
      ...markup,
      html: [...markup.html, payload.creative.markup],
      children: [
        ...markup.children,
        createIframeSrcdoc({
          clazz: 'mbk-griffin',
          width: '300px',
          height: '250px',
          markup: payload.rtb?.markup || '',
          hide: true,
        }),
      ],
    });
  }

  // It's RTB creative if creative.markup is blank
  return fromValue({
    ...markup,
    html: [...markup.html, payload.rtb?.markup || ''],
  });
};

const getAdServerEndpoint = () => {
  const { endpoint } = getOptions();

  switch (endpoint) {
    case 'tagservice.local.mobkoi.com':
      return 'adserver.local.mobkoi.com';
    case 'tagservice.dev.mobkoi.com':
      return 'adserver.dev.mobkoi.com';
    default:
      return 'adserver.maximus.mobkoi.com';
  }
};

export const getEventsServerEndpoint = () => {
  const { endpoint } = getOptions();

  switch (endpoint) {
    case 'tagservice.local.mobkoi.com':
      return 'events.dev.mobkoi.com';
    case 'tagservice.dev.mobkoi.com':
      return 'events.dev.mobkoi.com';
    default:
      return 'events.mobkoi.com';
  }
};

/**
 * Get all macro data from the give tag
 * @param tag
 * @returns
 */
const getMacroData = (tag: Tag) => {
  const { domain, doNotTrack } = getEnvironment();
  const { pageOptions, payload, tagId, container, creativeContainer } = tag;
  const { mobkoi, impressionId, creative, geo, user } = payload!;
  const { constrainedMaxSize } = getPageSizeDetails(tag);

  return {
    cacheBuster: buster(),
    impressionId,
    tagId,
    lineItemId: mobkoi.lineItemId,
    lineItemName: mobkoi.lineItemName,
    bookingId: mobkoi.bookingId,
    advertiserId: mobkoi.advertiserId,
    publisherId: mobkoi.publisherId,
    domain: domain,
    campaignId: mobkoi.campaignId,
    placementId: mobkoi.placementId,
    creativeId: mobkoi.creativeId,
    adId: mobkoi.adId,
    width: constrainedMaxSize.width,
    height: constrainedMaxSize.height,
    size: `${constrainedMaxSize.width}x${constrainedMaxSize.height}`,
    format: creative.internalFormat || creative.clazz,
    reportingName: creative.reportingName,
    creativeName: creative.creativeName,
    country: geo.country.name,
    countryCode: geo.country.isoCode,
    platform: getUserPlatform(user),
    language: user.language,
    office: mobkoi.campaignOffice,
    campaignName: mobkoi.campaignName,
    mobkoiAdContainer: container?.id,
    mobkoiAdCreativeContainer: creativeContainer?.id,
    // GDPR is handled above for the usual consent string format `${GDPR_CONSENT_NUMBER}`
    // these are here for macro format consistency with other values
    gdprConsent: pageOptions.gdpr_consent,
    gdprApplies: pageOptions.gdpr,
    // Do not track is set by the users browser
    doNotTrack,
    adServerEndpoint: getAdServerEndpoint(),
    eventsServerEndpoint: getEventsServerEndpoint(),
  };
};

const handleCustomMarkup = (tag: Tag) => {
  const { pageOptions, payload } = tag;
  const { mobkoi } = payload!;

  const replaceGDPRConsent = (markup: string) =>
    pageOptions.gdpr_consent
      ? markup.replace(RE_GDPR_CONSENT, pageOptions.gdpr_consent)
      : markup;

  const replaceGDPROption = (markup: string) =>
    pageOptions.hasOwnProperty('gdpr')
      ? markup.replace(RE_GDPR, pageOptions.gdpr)
      : markup;

  const mobkoiMarkup = replaceGDPROption(
    replaceGDPRConsent(mobkoi.markup || ''),
  );

  const macroData = getMacroData(tag);
  const customMarkup = processMacros(macroData, mobkoiMarkup);

  return customMarkup;
};

const buildCustomUrlsAndHtml = (customMarkup: string) => {
  let customHtml: string[] = [];
  const customUrls: Element[] = [];

  csvToArray(customMarkup, '\n').forEach((string) => {
    if (RE_URL_TEST.test(string)) {
      // fix - Moat and IAS pixels may be included as custom code but they both need to be delivered into the same
      // place on the page, otherwise we get discrepancies (eg. Equinor on Reuters). So if we're rendering to
      // our container then we'll treat script trackers as urls which we'll append to our container, whereas
      // in all other cases we'll turn them into SCRIPT tags and the whole lot is included in the IFRAME
      // srcdoc html. In both cases all custom stuff sits at the same level as the Celtra creative
      // (see visualMarkup below).
      customUrls.push(
        Object.assign(document.createElement('script'), {
          type: 'text/javascript',
          async: 1,
          src: string,
        }),
      );
    } else {
      customHtml.push(string);
    }
  });

  return {
    customHtml,
    customUrls,
  };
};

// our click tracker urls can come from multiple sources and we'll apply some macros to them.
// for now we treat them all as pixels, but later we may/will support daisy-chains and maybe
// scripts.
const handleClickTrackers = (tag: Tag) => {
  const payload = tag.payload!;
  if (payload.creative.placementId) {
    return buildCeltraClickTrackers(tag);
  }

  return [];
};

export const handleTrackers = (tag: Tag) => {
  const payload = tag.payload!;
  if (payload.trackers) {
    const macroData = getMacroData(tag);

    return payload.trackers.map((currentTracker) => {
      return {
        ...currentTracker,
        tracker: processMacros(macroData, currentTracker.tracker),
      };
    });
  }

  return [];
};

/**
 * This builds all the markup templates we need down the chain. This emits
 * {html: [], children: [], clickTrackers:[], triggers:[]} (see getMarkupTemplate() types)
 * We pass this object from one function to the next adding the relevant values
 * to each array, the consumer of this is responsible for stitching and rendering
 * the values of the arrays to the page correctly
 */
export const buildCreativeValues$ = (tag: Tag) =>
  pipe(
    fromValue(getMarkupTemplates()),
    mergeMap((markupTemplates) => handleCreativeTypes$(tag, markupTemplates)),
    map((markupTemplates) => {
      const customMarkup = handleCustomMarkup(tag);
      const { customHtml, customUrls } = buildCustomUrlsAndHtml(customMarkup);

      // This looks a little mad, but just ensuring we don't accidentally mutate
      // any values as that's where 🐉's lie.
      const markup = {
        ...markupTemplates,
        html: [...markupTemplates.html, ...customHtml],
        children: [...markupTemplates.children, ...customUrls],
        clickTrackers: [
          ...markupTemplates.clickTrackers,
          ...handleClickTrackers(tag),
        ],
        trackers: [...markupTemplates.trackers, ...handleTrackers(tag)],
      };

      // Return the current tag instance for consumer too - keep that flow going
      return {
        tag,
        markupTemplates: markup,
      };
    }),
  );
